import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'

interface GaugeChartProps {
  value: number
  min?: number
  max?: number
  width?: number
  height?: number
  title?: string
  label?: string
  unit?: string
  thresholds?: { value: number; color: string }[]
  animate?: boolean
}

export const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  min = 0,
  max = 100,
  width = 300,
  height = 200,
  title,
  label,
  unit = '',
  thresholds = [
    { value: 33, color: '#E74C3C' },
    { value: 66, color: '#F59E0B' },
    { value: 100, color: '#50C878' }
  ],
  animate = true
}) => {
  const svgRef = useRef<SVGSVGElement>(null)

  useEffect(() => {
    if (!svgRef.current) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const radius = Math.min(width, height * 2) / 2 - 20
    const innerRadius = radius * 0.7

    const centerX = width / 2
    const centerY = height - 30

    // Normalize value
    const normalizedValue = Math.max(min, Math.min(max, value))
    const percentage = (normalizedValue - min) / (max - min)
    const angle = (percentage * Math.PI) - Math.PI / 2

    // Create arc generator
    const arcGenerator = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(radius)
      .cornerRadius(4)

    const g = svg.append('g')
      .attr('transform', `translate(${centerX},${centerY})`)

    // Background arc
    g.append('path')
      .attr('d', arcGenerator({
        startAngle: -Math.PI / 2,
        endAngle: Math.PI / 2
      } as any))
      .attr('fill', '#F3F4F6')

    // Threshold segments
    let previousAngle = -Math.PI / 2
    thresholds.forEach((threshold, i) => {
      const thresholdPercentage = (threshold.value - min) / (max - min)
      const thresholdAngle = (thresholdPercentage * Math.PI) - Math.PI / 2

      const segment = g.append('path')
        .attr('fill', threshold.color)
        .attr('opacity', 0.3)

      if (animate) {
        segment
          .attr('d', arcGenerator({
            startAngle: previousAngle,
            endAngle: previousAngle
          } as any))
          .transition()
          .duration(600)
          .delay(i * 100)
          .attrTween('d', function() {
            const interpolate = d3.interpolate(previousAngle, thresholdAngle)
            const startAngle = previousAngle
            return (t) => arcGenerator({
              startAngle,
              endAngle: interpolate(t)
            } as any) || ''
          })
      } else {
        segment.attr('d', arcGenerator({
          startAngle: previousAngle,
          endAngle: thresholdAngle
        } as any))
      }

      previousAngle = thresholdAngle
    })

    // Value arc
    const currentColor = thresholds.reduce((color, threshold) =>
      normalizedValue <= threshold.value ? color : threshold.color,
      thresholds[0]?.color || '#4A90E2'
    )

    const valueArc = g.append('path')
      .attr('fill', currentColor)

    if (animate) {
      valueArc
        .attr('d', arcGenerator({
          startAngle: -Math.PI / 2,
          endAngle: -Math.PI / 2
        } as any))
        .transition()
        .duration(1000)
        .ease(d3.easeElasticOut.amplitude(1).period(0.4))
        .attrTween('d', function() {
          const interpolate = d3.interpolate(-Math.PI / 2, angle)
          return (t) => arcGenerator({
            startAngle: -Math.PI / 2,
            endAngle: interpolate(t)
          } as any) || ''
        })
    } else {
      valueArc.attr('d', arcGenerator({
        startAngle: -Math.PI / 2,
        endAngle: angle
      } as any))
    }

    // Needle
    const needleLength = innerRadius - 10
    const needleWidth = 6

    const needlePath = [
      { x: 0, y: -needleLength },
      { x: needleWidth / 2, y: 0 },
      { x: -needleWidth / 2, y: 0 }
    ]

    const lineGenerator = d3.line<{ x: number; y: number }>()
      .x(d => d.x)
      .y(d => d.y)

    const needle = g.append('path')
      .attr('d', lineGenerator(needlePath))
      .attr('fill', '#1F2937')

    if (animate) {
      needle
        .attr('transform', 'rotate(-90)')
        .transition()
        .duration(1000)
        .ease(d3.easeElasticOut.amplitude(1).period(0.4))
        .attr('transform', `rotate(${(percentage * 180) - 90})`)
    } else {
      needle.attr('transform', `rotate(${(percentage * 180) - 90})`)
    }

    // Center circle
    g.append('circle')
      .attr('r', 10)
      .attr('fill', '#1F2937')

    // Min/Max labels
    g.append('text')
      .attr('x', -radius - 5)
      .attr('y', 15)
      .attr('text-anchor', 'end')
      .style('font-size', '11px')
      .style('fill', '#6B7280')
      .text(min.toString())

    g.append('text')
      .attr('x', radius + 5)
      .attr('y', 15)
      .attr('text-anchor', 'start')
      .style('font-size', '11px')
      .style('fill', '#6B7280')
      .text(max.toString())

    // Value display
    const valueText = g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', -radius / 4)
      .style('font-size', '24px')
      .style('font-weight', '700')
      .style('fill', currentColor)

    if (animate) {
      valueText
        .text(min.toString() + unit)
        .transition()
        .duration(1000)
        .tween('text', function() {
          const i = d3.interpolateNumber(min, normalizedValue)
          return (t) => {
            this.textContent = Math.round(i(t)).toString() + unit
          }
        })
    } else {
      valueText.text(normalizedValue.toString() + unit)
    }

    // Label
    if (label) {
      g.append('text')
        .attr('text-anchor', 'middle')
        .attr('y', -radius / 4 + 25)
        .style('font-size', '12px')
        .style('fill', '#6B7280')
        .text(label)
    }

    // Title
    if (title) {
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '14px')
        .style('font-weight', '600')
        .style('fill', '#1F2937')
        .text(title)
    }

  }, [value, min, max, width, height, title, label, unit, thresholds, animate])

  return <svg ref={svgRef} width={width} height={height} />
}

export default GaugeChart
